// The arithmetic behind the Offerings summary. Pure functions over donations
// and collections, so the screen only lays the answers out.
//
// Only an explicit VOID leaves the figures: a row awaiting a deletion decision
// is still counted, as it is everywhere else in the finance screens.

import { amountOf, dateOf, isoOf, lastMonths, recent, sumOf, totalBy } from '../../utils/summaryStats';
import type { NamedTotal } from '../../utils/summaryStats';

export interface DonationLike {
  receiptNo?: string;
  date?: string;
  donorName?: string;
  purpose?: string;
  amount?: unknown;
  status?: string;
}

export interface CollectionLike {
  receiptId?: string;
  date?: string;
  source?: string;
  givenBy?: string;
  amount?: unknown;
  countedBy?: string;
  verifiedBy?: string;
  status?: string;
}

const isVoid = (r: { status?: string }) => r.status === 'VOID';

/** April–March, as '2026-2027', from an ISO date; null when there is no date. */
export const fyOfIso = (v: unknown): string | null => {
  const d = dateOf(v);
  if (!d) return null;
  const y = Number(d.slice(0, 4));
  return Number(d.slice(5, 7)) >= 4 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
};

/** Sunday School offerings are mirrored into Donations with this purpose prefix. */
export const isSundaySchool = (d: DonationLike): boolean =>
  /^sunday school offering/i.test(String(d.purpose || '').trim());

const isSunday = (v: unknown): boolean => {
  const d = dateOf(v);
  if (!d) return false;
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).getDay() === 0;
};

/** A donor worth ranking: a real name, not "Anonymous" or a Sunday School class. */
const isNamedDonor = (d: DonationLike): boolean => {
  const name = String(d.donorName || '').trim();
  return Boolean(name) && !/^anonymous$/i.test(name) && !isSundaySchool(d);
};

export interface OfferingMonth {
  month: string;
  label: string;
  donations: number;
  collections: number;
  total: number;
  count: number;
}

export interface SundayTotal { date: string; total: number; count: number; }

export interface OfferingSummary {
  fy: string;
  thisMonth: number;
  lastMonth: number;
  fyTotal: number;
  fyCount: number;
  fyDonations: number;
  fyCollections: number;
  sundaySchool: { count: number; total: number };
  /** Average of each Sunday's collection total over the last 12 weeks, or null. */
  avgSunday: number | null;
  /** The recent Sundays behind that average, newest first. */
  sundays: SundayTotal[];
  donorsThisFy: number;
  allTime: number;
  liveCount: number;
  voided: number;
  months: OfferingMonth[];
  byPurpose: NamedTotal[];
  bySource: NamedTotal[];
  topDonors: NamedTotal[];
  unverified: CollectionLike[];
}

export const offeringSummary = (
  donations: DonationLike[], collections: CollectionLike[], now: Date = new Date()
): OfferingSummary => {
  const liveD = donations.filter((d) => !isVoid(d));
  const liveC = collections.filter((c) => !isVoid(c));
  const fy = fyOfIso(isoOf(now)) as string;
  const inFy = <T extends { date?: string }>(rows: T[]) => rows.filter((r) => fyOfIso(r.date) === fy);
  const fyD = inFy(liveD);
  const fyC = inFy(liveC);

  const thisYm = isoOf(now).slice(0, 7);
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastYm = isoOf(prev).slice(0, 7);
  const monthTotal = (ym: string) =>
    sumOf([...liveD, ...liveC].filter((r) => (dateOf(r.date) || '').startsWith(ym)), (r) => r.amount);

  // Donations and collections are bucketed apart, then laid side by side.
  const dm = lastMonths(liveD, (r) => r.date, 12, now, (r) => r.amount);
  const cm = lastMonths(liveC, (r) => r.date, 12, now, (r) => r.amount);
  const months = dm.map((b, i) => ({
    month: b.month,
    label: b.label,
    donations: b.total,
    collections: cm[i].total,
    total: Math.round((b.total + cm[i].total) * 100) / 100,
    count: b.count + cm[i].count
  }));

  // One Sunday can have several collections (worship, communion…); add them up.
  const perSunday = new Map<string, SundayTotal>();
  recent(liveC, (c) => c.date, 84, now).filter((c) => isSunday(c.date)).forEach((c) => {
    const d = dateOf(c.date) as string;
    const cur = perSunday.get(d) || { date: d, total: 0, count: 0 };
    cur.total += amountOf(c.amount);
    cur.count += 1;
    perSunday.set(d, cur);
  });
  const sundays = Array.from(perSunday.values())
    .map((s) => ({ ...s, total: Math.round(s.total * 100) / 100 }))
    .sort((a, b) => b.date.localeCompare(a.date));

  const ss = fyD.filter(isSundaySchool);
  const fyDonations = sumOf(fyD, (r) => r.amount);
  const fyCollections = sumOf(fyC, (r) => r.amount);
  const topDonors = totalBy(fyD.filter(isNamedDonor), (d) => String(d.donorName).trim(), (d) => d.amount);

  return {
    fy,
    thisMonth: monthTotal(thisYm),
    lastMonth: monthTotal(lastYm),
    fyTotal: Math.round((fyDonations + fyCollections) * 100) / 100,
    fyCount: fyD.length + fyC.length,
    fyDonations,
    fyCollections,
    sundaySchool: { count: ss.length, total: sumOf(ss, (r) => r.amount) },
    avgSunday: sundays.length
      ? Math.round(sundays.reduce((n, s) => n + s.total, 0) / sundays.length)
      : null,
    sundays,
    donorsThisFy: topDonors.length,
    allTime: Math.round((sumOf(liveD, (r) => r.amount) + sumOf(liveC, (r) => r.amount)) * 100) / 100,
    liveCount: liveD.length + liveC.length,
    voided: donations.filter(isVoid).length + collections.filter(isVoid).length,
    months,
    byPurpose: totalBy(fyD, (d) => (isSundaySchool(d) ? 'Sunday School offering' : d.purpose), (d) => d.amount),
    bySource: totalBy(fyC, (c) => c.source, (c) => c.amount),
    topDonors: topDonors.slice(0, 10),
    unverified: liveC
      .filter((c) => !String(c.verifiedBy || '').trim())
      .sort((a, b) => String(dateOf(b.date)).localeCompare(String(dateOf(a.date))))
  };
};
